// Helpers puros del conteo facetado, compartidos por el Avivador y la home
// (Ajuste #16). Sin JSX ni DOM: se testean en facetas.test.ts (vitest, node).
// Nada específico de una página acá adentro: las dimensiones, los valores y
// la unidad del conteo los arma cada página.
import type { ConteoFacetas, DimensionFaceta, UnidadConteo } from './tipos';

/** Enteros con separador de miles es-AR («1.091»). */
const fmtMiles = new Intl.NumberFormat('es-AR', { maximumFractionDigits: 0 });

/**
 * Conteo facetado cruzado: para cada dimensión `d`, recorre `items` y, por cada
 * ítem que pasa TODAS las demás dimensiones (`o.pasa(item)` para toda `o ≠ d`),
 * suma 1 en `resultado[d.id][v]` por cada `v` de `d.valores(item)`. El filtro
 * de `d` no se aplica a su propio conteo. Para un «total sin X» (Ramas «n/total»
 * del Avivador), se llama otra vez sin la dimensión X.
 *
 * Toda dimensión de `dims` tiene su clave en el resultado (aunque quede `{}`);
 * solo aparecen los valores con al menos 1. Ids repetidos: gana la última.
 */
export function contarFacetas<I>(items: readonly I[], dims: readonly DimensionFaceta<I>[]): ConteoFacetas {
  // Un Map por dimensión (por posición, no por id: dos dimensiones con el
  // mismo id no se mezclan; al final gana la última). Map y no objeto: un
  // valor como «constructor» o «__proto__» no choca con el prototipo.
  const porDim: Map<string, number>[] = dims.map(() => new Map());

  for (const item of items) {
    // `pasa` se evalúa una sola vez por ítem y dimensión.
    const pasa = dims.map(d => d.pasa(item));
    let fallas = 0;
    let unicaFalla = -1;
    for (let k = 0; k < pasa.length; k++) {
      if (!pasa[k]) {
        fallas++;
        unicaFalla = k;
      }
    }
    // Con 2 o más dimensiones que no pasa, no suma en ninguna: para cada `d`
    // queda al menos otra dimensión que lo recorta.
    if (fallas >= 2) continue;

    for (let k = 0; k < dims.length; k++) {
      // Pasa todas las OTRAS: o no falla ninguna, o la única que falla es esta.
      if (fallas === 1 && unicaFalla !== k) continue;
      const cuenta = porDim[k];
      for (const v of dims[k].valores(item)) cuenta.set(v, (cuenta.get(v) ?? 0) + 1);
    }
  }

  const resultado: ConteoFacetas = {};
  dims.forEach((d, k) => {
    // Object.fromEntries define propiedades propias (también «__proto__»).
    resultado[d.id] = Object.fromEntries(porDim[k]);
  });
  return resultado;
}

/** Selección múltiple: saca `valor` si está, lo agrega al final si no. Siempre
 *  un array nuevo. */
export function alternarValor<V>(lista: readonly V[], valor: V): V[] {
  return lista.includes(valor) ? lista.filter(v => v !== valor) : [...lista, valor];
}

/** ¿Se muestra el total? Solo si hay total y es mayor que el conteo. */
function conTotal(conteo: number, total: number | undefined): total is number {
  return total !== undefined && total > conteo;
}

/** Texto visible del conteo de una opción: «21/41» si `total > conteo`; si no
 *  (o sin total), «21». Miles con separador es-AR («1.091»). */
export function textoConteo(conteo: number, total?: number): string {
  if (conTotal(conteo, total)) return `${fmtMiles.format(conteo)}/${fmtMiles.format(total)}`;
  return fmtMiles.format(conteo);
}

/**
 * Conteo para lectores de pantalla (va en `sr-only`; el visible es `aria-hidden`):
 * - con `total > conteo`: «21 de 41 redes»;
 * - si no: «21 redes».
 * La unidad concuerda con el número que la precede (el total si lo hay): «1
 * rede», «21 redes», «0 de 1 rede», «1 de 41 redes». Sin `unidad`: «21 de 41»
 * / «21». Miles es-AR.
 */
export function conteoSr(conteo: number, total?: number, unidad?: UnidadConteo): string {
  const conUnidad = (n: number): string => (unidad ? ` ${n === 1 ? unidad.singular : unidad.plural}` : '');
  if (conTotal(conteo, total)) {
    return `${fmtMiles.format(conteo)} de ${fmtMiles.format(total)}${conUnidad(total)}`;
  }
  return `${fmtMiles.format(conteo)}${conUnidad(conteo)}`;
}
